import type { OpcUaNodeIdForm } from "../../../../../interfaces/AdapterCdoOpcUa.interface";

/** Marker an operator puts in a Node Identifier; each floor of a range replaces it with its own number. */
export const FLOOR_PLACEHOLDER = "{floor}";

/** The part of a mapping row a floor range reads and writes. */
interface FloorRow {
  msgId: string;
  node: OpcUaNodeIdForm;
  position: number | null;
}

/**
 * Highest floor index an adapter with `floors` floors can address. Floors are
 * LN2 indices counted from 0, so N floors top out at N-1. `ceiling` is the
 * wire limit (uint16) and also what applies when the adapter's count is unknown.
 */
export function maxFloorPosition(floors: number | undefined, ceiling: number): number {
  if (floors === undefined || !Number.isFinite(floors)) return ceiling;
  return Math.min(ceiling, Math.max(0, Math.floor(floors) - 1));
}

/**
 * Why a range `start`..`end` cannot be created, or null when it can.
 * `requirePlaceholder` is for inbound rows, whose lookup key is node + value:
 * without `{floor}` in the identifier every floor would share one key.
 */
export function floorRangeProblem(
  draft: FloorRow,
  start: number | null,
  end: number | null,
  maxPosition: number,
  requirePlaceholder: boolean,
): string | null {
  if (start === null) return "Enter the first floor of the range.";
  if (end === null) return "Enter the last floor of the range.";
  if (end < start) return "The last floor must be the same as or higher than the first floor.";
  if (start > maxPosition || end > maxPosition) {
    return `This adapter's floors only go up to floor ${maxPosition}.`;
  }
  if (requirePlaceholder && !draft.node.identifier.includes(FLOOR_PLACEHOLDER)) {
    return `Node Identifier must contain ${FLOOR_PLACEHOLDER} so each floor gets its own node (for example Lift.CarCall${FLOOR_PLACEHOLDER}, or 100${FLOOR_PLACEHOLDER} for a numeric id).`;
  }
  return null;
}

/** One row per floor from `start` to `end` inclusive, `{floor}` in the identifier replaced by that floor. */
export function expandFloorRange<T extends FloorRow>(draft: T, start: number, end: number): T[] {
  const rows: T[] = [];
  for (let floor = start; floor <= end; floor += 1) {
    rows.push({
      ...draft,
      position: floor,
      node: {
        ...draft.node,
        identifier: draft.node.identifier.split(FLOOR_PLACEHOLDER).join(String(floor)),
      },
    });
  }
  return rows;
}

/** True for a stored row of the same message that sits inside the range — the rows a new range replaces. */
function isReplacedByRange(row: FloorRow, msgId: string, start: number, end: number): boolean {
  return (
    row.msgId === msgId && row.position !== null && row.position >= start && row.position <= end
  );
}

/** How many stored rows creating this range would replace. */
export function countReplacedByRange(
  existing: FloorRow[],
  msgId: string,
  start: number,
  end: number,
): number {
  return existing.filter((row) => isReplacedByRange(row, msgId, start, end)).length;
}

/** `existing` without the rows of this message inside the range, followed by the freshly built range. */
export function replaceRange<T extends FloorRow>(
  existing: T[],
  created: T[],
  msgId: string,
  start: number,
  end: number,
): T[] {
  return [...existing.filter((row) => !isReplacedByRange(row, msgId, start, end)), ...created];
}
